export type Locale = "en" | "fr";

export const TRANSLATIONS = {
  en: {
    "app.title": "Sherlock",
    "app.subtitle": "An AKQA tool",
    "upload.title": "Sherlock",
    "upload.tagline":
      "Upload your transcripts, briefs, or reports to reveal the hidden map of insights.",
    "upload.drop": "Drag & drop documents",
    "upload.dropping": "Drop the files here",
    "upload.formats": "Supports .txt, .md, .csv, .pdf files. Multiple files allowed.",
    "upload.parsingPdf": "Parsing PDFs…",
    "upload.demoCta": "Don't have transcripts? Try the Demo",
    "upload.resumeCta": "Resume previous session",
    "context.heading": "Setting the Stage",
    "context.subheading":
      "Tell Sherlock about the material you've uploaded to sharpen the analysis.",
    "context.speakers": "Describe the context",
    "context.speakers.placeholder":
      "Briefly describe who is speaking, what documents you have provided, and any relevant context for the analysis.",
    "context.goals": "What are you looking for?",
    "context.goals.placeholder":
      "Are you after opportunities, insights, tensions between stakeholders? Any specific information or hypothesis you are trying to verify?",
    "context.views": "Which views should Sherlock generate?",
    "context.submit": "Begin Analysis",
    "thinking.title": "Sherlock is thinking",
    "thinking.steps.examining": "Examining the interview transcripts...",
    "thinking.steps.understanding": "Understanding the stakes...",
    "thinking.steps.reading": "Reading between the lines...",
    "thinking.steps.confronting": "Confronting opinions...",
    "thinking.steps.building": "Building the relation map...",
    "thinking.steps.pulling": "Pulling the most relevant and defining quotes...",
    "thinking.cancel": "Cancel analysis",
    "thinking.cancelled": "Analysis cancelled.",
    "feedback.prompt": "Was this analysis useful?",
    "feedback.thanks": "Thanks for the feedback.",
    "map.viewSelector": "View selector",
    "map.askSherlock": "Ask Sherlock",
    "map.search": "Search...",
    "map.export": "Export",
    "map.exportJson": "Export JSON",
    "map.exportPng": "Export PNG",
    "views.graph": "Relationships",
    "views.graph.desc": "Interactive visualization of strategic insights.",
    "views.compass": "Compass",
    "views.compass.desc": "Stakeholders mapped by core perspectives.",
    "views.topics": "Themes",
    "views.topics.desc": "Key discussion themes and sentiment.",
    "views.table": "Stakeholders",
    "views.table.desc": "A structured list of all stakeholders.",
    "views.corpus": "Corpus",
    "views.corpus.desc": "Raw document archive and source material.",
    "chat.title": "Sherlock",
    "views.introduction": "Cover",
    "views.introduction.desc": "Opening paragraph framing the context and stakes.",
    "views.overview": "Synthesis",
    "views.overview.desc": "Executive synthesis and strategic opportunities.",
    "chat.subtitle": "Strategic Intelligence",
    "chat.placeholder": "Ask Sherlock...",
    "chat.empty": "Ask me anything about the analysis or upload more documents.",
    "chat.thinking": "Sherlock is thinking...",
    "chat.addMaterial": "Add new material",
    "chat.confirmReupload": "Add new material?",
    "chat.confirmReupload.desc":
      "Uploading additional documents will trigger a complete re-analysis. Previous analysis will be overwritten.",
    "chat.confirm": "Confirm & Upload",
    "chat.cancel": "Cancel",
    "error.title": "Sherlock hit a snag",
    "error.retry": "Try Again",
    "error.dismiss": "Dismiss",
    "error.report": "Strategic Intelligence Error Report",
  },
  fr: {
    "app.title": "Sherlock",
    "app.subtitle": "Un outil AKQA",
    "upload.title": "Sherlock",
    "upload.tagline":
      "Téléversez vos transcripts, briefs ou rapports pour révéler la cartographie cachée de vos insights.",
    "upload.drop": "Glissez-déposez vos documents",
    "upload.dropping": "Déposez les fichiers ici",
    "upload.formats":
      "Formats supportés : .txt, .md, .csv, .pdf. Plusieurs fichiers possibles.",
    "upload.parsingPdf": "Extraction des PDF…",
    "upload.demoCta": "Pas encore de transcripts ? Essayez la démo",
    "upload.resumeCta": "Reprendre la session précédente",
    "context.heading": "Plantons le décor",
    "context.subheading":
      "Donnez à Sherlock le contexte de vos documents pour affiner l'analyse.",
    "context.speakers": "Décrivez le contexte",
    "context.speakers.placeholder":
      "Qui s'exprime ? Quels documents avez-vous fournis ? Toute information utile avant l'analyse.",
    "context.goals": "Qu'est-ce que vous cherchez ?",
    "context.goals.placeholder":
      "Opportunités, insights, tensions entre parties prenantes ? Hypothèses à valider ?",
    "context.views": "Quelles vues Sherlock doit-il générer ?",
    "context.submit": "Lancer l'analyse",
    "thinking.title": "Sherlock réfléchit",
    "thinking.steps.examining": "Examen des transcripts d'entretiens...",
    "thinking.steps.understanding": "Compréhension des enjeux...",
    "thinking.steps.reading": "Lecture entre les lignes...",
    "thinking.steps.confronting": "Confrontation des opinions...",
    "thinking.steps.building": "Construction de la carte des relations...",
    "thinking.steps.pulling": "Sélection des citations les plus marquantes...",
    "thinking.cancel": "Annuler l'analyse",
    "thinking.cancelled": "Analyse annulée.",
    "feedback.prompt": "Cette analyse était-elle utile ?",
    "feedback.thanks": "Merci pour votre retour.",
    "map.viewSelector": "Sélecteur de vue",
    "map.askSherlock": "Interroger Sherlock",
    "map.search": "Rechercher...",
    "map.export": "Exporter",
    "map.exportJson": "Exporter en JSON",
    "map.exportPng": "Exporter en PNG",
    "views.graph": "Relationships",
    "views.graph.desc": "Visualisation interactive des insights stratégiques.",
    "views.compass": "Compass",
    "views.compass.desc":
      "Parties prenantes positionnées selon leurs perspectives clés.",
    "views.topics": "Themes",
    "views.topics.desc": "Thèmes clés et analyse du sentiment.",
    "views.table": "Stakeholders",
    "views.table.desc": "Liste structurée de toutes les parties prenantes.",
    "views.corpus": "Corpus",
    "views.corpus.desc": "Documents source bruts.",
    "chat.title": "Sherlock",
    "views.introduction": "Cover",
    "views.introduction.desc":
      "Paragraphe d'ouverture cadrant le contexte et les enjeux.",
    "views.overview": "Synthesis",
    "views.overview.desc": "Synthèse exécutive et opportunités stratégiques.",
    "chat.subtitle": "Intelligence stratégique",
    "chat.placeholder": "Demandez à Sherlock...",
    "chat.empty":
      "Posez-moi vos questions sur l'analyse ou ajoutez de nouveaux documents.",
    "chat.thinking": "Sherlock réfléchit...",
    "chat.addMaterial": "Ajouter du contenu",
    "chat.confirmReupload": "Ajouter de nouveaux documents ?",
    "chat.confirmReupload.desc":
      "Téléverser de nouveaux documents relance une analyse complète. L'analyse précédente sera écrasée.",
    "chat.confirm": "Confirmer et téléverser",
    "chat.cancel": "Annuler",
    "error.title": "Sherlock a rencontré un souci",
    "error.retry": "Réessayer",
    "error.dismiss": "Fermer",
    "error.report": "Rapport d'erreur — Intelligence stratégique",
  },
} as const;

export type TranslationKey = keyof (typeof TRANSLATIONS)["en"];
